import { useEffect } from "react";
import { useBusiness } from "@/context/BusinessContext";

// Map our cuisine slug → schema.org @type. Covers the new bar / club /
// entertainment venue types added in Phase 18 so AEO crawlers (Google AI
// Overview, Bing Copilot, Perplexity) classify the venue correctly.
const SCHEMA_TYPE_BY_CUISINE = {
  cafe: "CafeOrCoffeeShop",
  bakery: "Bakery",
  pub: "BarOrPub",
  gastropub: "BarOrPub",
  rooftop_bar: "BarOrPub",
  cocktail_bar: "BarOrPub",
  wine_bar: "BarOrPub",
  craft_beer: "BarOrPub",
  whisky_bar: "BarOrPub",
  sports_bar: "BarOrPub",
  lounge_bar: "BarOrPub",
  nightclub: "NightClub",
  live_music: "NightClub",
  comedy_club: "EntertainmentBusiness",
  karaoke: "NightClub",
  bowling: "BowlingAlley",
  events_venue: "EventVenue",
};

// Days mapping for OpeningHoursSpecification.
const DAY_TO_SCHEMA = {
  mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday",
  fri: "Friday", sat: "Saturday", sun: "Sunday",
};

function buildOpeningHours(operating_hours = {}) {
  const out = [];
  for (const [day, windows] of Object.entries(operating_hours || {})) {
    const wkday = DAY_TO_SCHEMA[day];
    if (!wkday || !Array.isArray(windows)) continue;
    for (const w of windows) {
      if (!w?.start || !w?.end) continue;
      out.push({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: wkday,
        opens: w.start,
        closes: w.end,
      });
    }
  }
  return out;
}

export function buildLocalBusinessSchema(business, { url } = {}) {
  if (!business) return null;
  const cuisine = (business.cuisine || "").toLowerCase();
  const schemaType =
    SCHEMA_TYPE_BY_CUISINE[cuisine] ||
    (business.is_restaurant === false ? "LocalBusiness" : "Restaurant");

  const sameAs = Object.values(business.socials || {}).filter(
    (v) => typeof v === "string" && v.trim().startsWith("http"),
  );

  return {
    "@context": "https://schema.org",
    "@type": schemaType,
    name: business.business_name,
    url: url || window.location.origin,
    image: business.hero_image_url || undefined,
    logo: business.logo_url || undefined,
    telephone: business.phone || undefined,
    email: business.email || undefined,
    priceRange: business.price_range || "$$",
    servesCuisine:
      ["Restaurant", "CafeOrCoffeeShop", "FoodEstablishment"].includes(schemaType) && cuisine
        ? cuisine.replace(/_/g, " ")
        : undefined,
    address: {
      "@type": "PostalAddress",
      streetAddress: [business.address1, business.address2].filter(Boolean).join(" ").trim(),
      addressLocality: business.suburb,
      addressRegion: business.state,
      postalCode: business.postcode,
      addressCountry: "AU",
    },
    openingHoursSpecification: buildOpeningHours(business.operating_hours),
    sameAs: sameAs.length ? sameAs : undefined,
  };
}

function buildWebSiteSchema(business) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: business?.business_name || "",
    url: origin,
    potentialAction: {
      "@type": "SearchAction",
      target: `${origin}/menu?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

function pruneEmpty(obj) {
  if (Array.isArray(obj)) {
    const out = obj.map(pruneEmpty).filter((v) => v !== undefined && v !== null && v !== "");
    return out.length ? out : undefined;
  }
  if (obj && typeof obj === "object") {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      const pv = pruneEmpty(v);
      if (pv !== undefined && pv !== null && pv !== "") out[k] = pv;
    }
    return Object.keys(out).length ? out : undefined;
  }
  return obj;
}

/**
 * Injects JSON-LD into <head>. Accepts an optional `extras` array of schema
 * objects (FAQPage / Place / BreadcrumbList) so individual pages — like
 * /locations/:slug — can layer their own structured data on top of the
 * default LocalBusiness + WebSite pair.
 *
 * `scope` keys each batch of injected scripts so multiple SeoHead components
 * mounted at once (layout + page) don't fight each other or duplicate the
 * base LocalBusiness/WebSite schemas. The layout uses scope="layout" (base
 * only); pages may override with scope="page" + their own extras and the
 * layout's base is suppressed when a page-scoped SeoHead is present.
 */
export default function SeoHead({ extras = [], title, description, scope = "layout" }) {
  const { business } = useBusiness();

  useEffect(() => {
    if (title) document.title = title;
  }, [title]);

  useEffect(() => {
    if (!description) return;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, [description]);

  // Per-tenant Open Graph + Twitter Card tags.
  // Re-runs whenever the active tenant changes so a deep-link to a
  // tenant route renders a tenant-specific share card.
  useEffect(() => {
    if (!business) return;
    const origin = (typeof window !== "undefined" && window.location.origin) || "";
    const slug = business.slug || "";
    const name = business.business_name || "HustlePlate";
    const desc = (business.tagline || business.hero_subheadline || description ||
                  "Run your whole restaurant from one tab.").slice(0, 200);
    const ogImage = slug
      ? `${origin}/api/og/${encodeURIComponent(slug)}.png`
      : `${origin}/images/hero-dashboard.jpg`;
    const url = window.location.href;

    const tags = [
      ["property", "og:type",        "website"],
      ["property", "og:site_name",   "HustlePlate"],
      ["property", "og:title",       name],
      ["property", "og:description", desc],
      ["property", "og:image",       ogImage],
      ["property", "og:image:width", "1200"],
      ["property", "og:image:height","630"],
      ["property", "og:url",         url],
      ["name",     "twitter:card",        "summary_large_image"],
      ["name",     "twitter:title",       name],
      ["name",     "twitter:description", desc],
      ["name",     "twitter:image",       ogImage],
    ];

    const nodes = tags.map(([attr, key, value]) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
      el.setAttribute("data-og-tenant", "1");
      return el;
    });
    return () => {
      // Don't auto-remove on unmount; leaving the tags lets server-side
      // crawlers (Facebook scraper, OpenGraph.io etc.) still see them
      // even if React unmounts before they fetch.
      void nodes;
    };
  }, [business, description]);

  useEffect(() => {
    // Always remove any previously-injected scripts for THIS scope first.
    const clearScope = () =>
      document
        .querySelectorAll(`script[data-seo-head="${scope}"]`)
        .forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    clearScope();

    // When a page-scoped SeoHead is mounting, also wipe the layout-scoped
    // tags so the page's full schema set is the only one in <head>.
    if (scope === "page") {
      document
        .querySelectorAll('script[data-seo-head="layout"]')
        .forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    }

    // If a "page" scope is active, suppress the "layout" scope's base
    // schemas to avoid duplicate LocalBusiness/WebSite entries.
    const pageScopePresent = !!document.querySelector('script[data-seo-head="page"]');
    if (scope === "layout" && pageScopePresent) {
      return () => {};
    }

    const schemas = [
      pruneEmpty(buildLocalBusinessSchema(business)),
      pruneEmpty(buildWebSiteSchema(business)),
      ...extras.map(pruneEmpty),
    ].filter(Boolean);

    const nodes = schemas.map((schema) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-head", scope);
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      nodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    };
  }, [business, JSON.stringify(extras), scope]);

  return null;
}
